import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaComment,
  FaDownload,
  FaExclamationCircle,
  FaEye,
  FaPaperPlane,
  FaSync,
  FaThLarge,
  FaWhatsapp,
} from "react-icons/fa";
import {
  fetchCampaignDetails,
  fetchCampaignStatusData,
  getCampaignMetricCount,
  unwrapCampaignStatusPayload,
} from "../../api/campaigns.api";
import { businessPath } from "../../config/businessMenu";

const escapeCsvCell = (value) => {
  const text = value == null ? "" : String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const csvRowValue = (row, header) => {
  if (!row || typeof row !== "object") return "";
  if (row[header] != null && row[header] !== "") return row[header];
  const key = String(header || "").toLowerCase();
  if (key === "name" || key === "username") {
    return row.name || row.username || "";
  }
  if (["mobile", "phone", "phonenumber", "waid"].includes(key)) {
    return (
      row.mobile ||
      row.phoneNumber ||
      row.phone ||
      row.waid ||
      row.recipient_waid ||
      row.recipient_id ||
      ""
    );
  }
  if (key === "tags" || key === "tag") return row.tags || row.tag || "";
  return "";
};

const collectCsvRows = (campaign) => {
  if (Array.isArray(campaign?.exceldata) && campaign.exceldata.length) {
    return campaign.exceldata;
  }
  if (Array.isArray(campaign?.audience) && campaign.audience.length) {
    return campaign.audience;
  }
  return [];
};

const buildCampaignCsv = (headers, rows) => {
  const cols = headers.length
    ? headers
    : rows[0]
      ? Object.keys(rows[0]).filter((key) => key !== "_id")
      : ["name", "mobile", "tags"];
  const lines = [cols.map(escapeCsvCell).join(",")];
  rows.forEach((row) => {
    lines.push(cols.map((header) => escapeCsvCell(csvRowValue(row, header))).join(","));
  });
  return `${lines.join("\r\n")}\r\n`;
};

const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const pct = (part, total) => {
  if (!total) return "0%";
  return `${((part / total) * 100).toFixed(1)}%`;
};

const formatCreatedAt = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "—";
  }
};

const getMessageType = (campaign) => {
  const tpl = campaign?.templateAssociate?.[0];
  const header = tpl?.components?.find((c) => String(c.type || "").toUpperCase() === "HEADER");
  const format = header?.format || "TEXT";
  return `Template · ${format}`;
};

const formatCampaignType = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  const labels = {
    CSVBROADCAST: "CSV Broadcast",
    "CSV Broadcast": "CSV Broadcast",
    WHATSAPP_SMS_CSVBROADCAST: "WhatsApp + SMS",
    "Multi Channel": "WhatsApp + SMS",
    APIBROADCAST: "API Broadcast",
    BROADCAST: "Broadcast",
  };
  return labels[raw] || raw.replace(/_/g, " ");
};

const getTemplatePreview = (template) => {
  const components = Array.isArray(template?.components) ? template.components : [];
  const header = components.find((c) => String(c.type || "").toUpperCase() === "HEADER");
  const body = components.find((c) => String(c.type || "").toUpperCase() === "BODY");
  const buttons = components.find((c) => String(c.type || "").toUpperCase() === "BUTTONS");
  return {
    name: template?.name || "",
    header: header?.text || "",
    format: header?.format || "TEXT",
    body: body?.text || "",
    buttons: Array.isArray(buttons?.buttons)
      ? buttons.buttons.map((item) => item.text).filter(Boolean)
      : [],
  };
};

const statusTone = (status) => {
  const value = String(status || "").toUpperCase();
  if (["SENT", "DELIVERED", "COMPLETED", "READ"].includes(value)) return "success";
  if (["FAILED", "ERROR"].includes(value)) return "danger";
  if (["SCHEDULED", "QUEUED", "PENDING"].includes(value)) return "warning";
  return "neutral";
};

const CampaignDetail = () => {
  const { projectId, campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const loadDetails = useCallback(async (isRefresh = false) => {
    if (!campaignId) return;
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const res = await fetchCampaignDetails(campaignId);
      setCampaign(res);
    } catch (err) {
      setCampaign(null);
      setError(err.message || "Failed to load campaign details.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [campaignId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const metrics = useMemo(() => {
    if (!campaign) return null;
    const submitted =
      campaign.submitteddata ??
      getCampaignMetricCount(campaign, "audience", "audienceCount") ??
      0;
    const sent = getCampaignMetricCount(campaign, "sent", "sentCount");
    const delivered = getCampaignMetricCount(campaign, "delivered", "deliveredCount");
    const read = getCampaignMetricCount(campaign, "read", "readCount");
    const failed = getCampaignMetricCount(campaign, "failed", "failedCount");
    const replied = getCampaignMetricCount(campaign, "replied", "repliedCount");
    const audience =
      campaign.audienceCount ??
      (Array.isArray(campaign.audience) ? campaign.audience.length : null) ??
      sent;
    const base = submitted || 1;

    return {
      submitted,
      sent,
      delivered,
      read,
      failed,
      replied,
      audience,
      payments: campaign.payments?.length ?? campaign.paymentCount ?? 0,
      totalCredits: campaign.totalCredits ?? campaign.totalcredits ?? null,
      status: campaign.campaignstatus || "—",
      funnel: [
        {
          id: "submitted",
          label: "Submitted",
          icon: FaThLarge,
          pct: pct(submitted, submitted),
          count: submitted,
          tone: "green",
        },
        {
          id: "sent",
          label: "Sent",
          icon: FaPaperPlane,
          pct: pct(sent, base),
          count: sent,
          tone: "blue",
        },
        {
          id: "delivered",
          label: "Delivered",
          icon: FaCheckCircle,
          pct: pct(delivered, base),
          count: delivered,
          tone: "yellow",
        },
        {
          id: "read",
          label: "Read",
          icon: FaEye,
          pct: pct(read, base),
          count: read,
          tone: "purple",
        },
        {
          id: "failed",
          label: "Failed",
          icon: FaExclamationCircle,
          pct: pct(failed, base),
          count: failed,
          tone: "red",
        },
        {
          id: "replied",
          label: "Replied",
          icon: FaComment,
          pct: pct(replied, base),
          count: replied,
          tone: "gold",
        },
      ],
    };
  }, [campaign]);

  const template = campaign?.templateAssociate?.[0];
  const templatePreview = getTemplatePreview(template);
  const templateImageUrl = campaign?.templatemediaurl?.default || null;
  const csvDownloadUrl = campaign?.csvuploadedfileurl || campaign?.csvfileurl || null;
  const csvHeaders = useMemo(() => {
    if (!campaign) return [];
    const fromHeaders = campaign.csvheaders;
    if (Array.isArray(fromHeaders) && fromHeaders.length > 0) {
      return fromHeaders.map((h) => (typeof h === "string" ? h : h?.name || h?.field || "")).filter(Boolean);
    }
    const mappings = campaign.headersMappings;
    if (Array.isArray(mappings) && mappings.length > 0) {
      return mappings.map((m) => m?.csvHeader || m?.field || m?.name || "").filter(Boolean);
    }
    return [];
  }, [campaign]);

  const canDownloadCsv = Boolean(
    csvDownloadUrl ||
      csvHeaders.length ||
      collectCsvRows(campaign).length,
  );

  const onDownloadCsv = async () => {
    if (!campaign || downloading) return;
    const filename = campaign.csvuploadfilename || campaign.csvfilename || "campaign.csv";
    setDownloading(true);
    setError("");
    try {
      if (csvDownloadUrl) {
        try {
          const res = await fetch(csvDownloadUrl);
          if (res.ok) {
            triggerBlobDownload(await res.blob(), filename);
            return;
          }
        } catch {
          /* generate from stored campaign rows instead */
        }
      }

      let rows = collectCsvRows(campaign);
      if (!rows.length && campaignId) {
        const statusRes = await fetchCampaignStatusData(campaignId, "submitted", {
          all: true,
        });
        rows = unwrapCampaignStatusPayload(statusRes).data;
      }
      if (!rows.length && !csvHeaders.length) {
        throw new Error("No CSV data available to download.");
      }
      const csv = buildCampaignCsv(csvHeaders, rows);
      triggerBlobDownload(new Blob([csv], { type: "text/csv;charset=utf-8;" }), filename);
    } catch (err) {
      setError(err.message || "Failed to download campaign CSV.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="campaign-detail-page">
      <header className="campaign-detail-topbar">
        <div className="campaign-detail-topbar-left">
          <Link
            to={businessPath(projectId, "fetchingallcampaign")}
            className="campaign-detail-back-icon"
            aria-label="Back to campaigns"
          >
            <FaArrowLeft />
          </Link>
          <div className="campaign-detail-heading">
            <h1>{campaign?.campaignName || "Campaign details"}</h1>
            {campaign && (
              <p className="campaign-detail-subtitle">
                {formatCampaignType(campaign.campaignType)}
                <span aria-hidden="true"> · </span>
                {formatCreatedAt(campaign.createdAt)}
              </p>
            )}
          </div>
        </div>
        <div className="campaign-detail-topbar-actions">
          {metrics?.status && metrics.status !== "—" && (
            <span className={`campaign-detail-status campaign-detail-status-${statusTone(metrics.status)}`}>
              {metrics.status}
            </span>
          )}
          <button
            type="button"
            className="campaign-detail-refresh"
            onClick={() => loadDetails(true)}
            disabled={loading || refreshing}
          >
            <FaSync className={refreshing ? "campaign-detail-spin" : ""} />
            Refresh
          </button>
        </div>
      </header>

      {error && <p className="campaign-detail-error">{error}</p>}
      {loading && !campaign && (
        <p className="campaign-detail-msg">Loading campaign details…</p>
      )}

      {campaign && metrics && (
        <div className="campaign-detail-body">
          <section className="campaign-detail-kpis" aria-label="Campaign performance">
            {metrics.funnel.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  to={businessPath(projectId, `campaign/${campaignId}/${item.id}`)}
                  className={`campaign-detail-kpi campaign-detail-kpi-${item.tone}`}
                >
                  <span className={`campaign-detail-kpi-icon campaign-funnel-icon-${item.tone}`}>
                    <Icon />
                  </span>
                  <span className="campaign-detail-kpi-copy">
                    <span className="campaign-detail-kpi-label">{item.label}</span>
                    <strong className="campaign-detail-kpi-value">{item.count}</strong>
                    <span className="campaign-detail-kpi-pct">{item.pct}</span>
                  </span>
                </Link>
              );
            })}
          </section>

          <div className="campaign-detail-panels">
            <section className="campaign-detail-card">
              <h2>Campaign details</h2>
              <dl className="campaign-detail-dl">
                <div>
                  <dt>Campaign type</dt>
                  <dd>{formatCampaignType(campaign.campaignType)}</dd>
                </div>
                <div>
                  <dt>Message type</dt>
                  <dd>{getMessageType(campaign)}</dd>
                </div>
                <div>
                  <dt>Template</dt>
                  <dd>{template?.name || "—"}</dd>
                </div>
                <div>
                  <dt>Audience</dt>
                  <dd>{metrics.audience}</dd>
                </div>
                <div>
                  <dt>Credits used</dt>
                  <dd>
                    {metrics.totalCredits != null && metrics.totalCredits !== ""
                      ? metrics.totalCredits
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Created</dt>
                  <dd>{formatCreatedAt(campaign.createdAt)}</dd>
                </div>
              </dl>

              <div className="campaign-detail-import">
                <div className="campaign-detail-import-row">
                  <div>
                    <span className="campaign-detail-import-label">Imported file</span>
                    <p className="campaign-detail-import-name">
                      {campaign.csvuploadfilename || campaign.csvfilename || "No file attached"}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="campaign-detail-download"
                    onClick={onDownloadCsv}
                    disabled={!canDownloadCsv || downloading}
                  >
                    <FaDownload />
                    {downloading ? "Downloading…" : "Download CSV"}
                  </button>
                </div>
                <span className="campaign-detail-import-label">CSV headers</span>
                {csvHeaders.length > 0 ? (
                  <div className="campaign-detail-csv-headers-list">
                    {csvHeaders.map((header) => (
                      <span key={header} className="campaign-detail-csv-header-pill">
                        {header}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="campaign-detail-csv-headers-empty">No headers recorded</p>
                )}
              </div>
            </section>

            <section className="campaign-detail-card">
              <div className="campaign-detail-preview-head">
                <h2>Template preview</h2>
                <span className="campaign-detail-preview-format">{templatePreview.format}</span>
              </div>
              <div className="campaign-detail-preview">
                <span className="campaign-detail-template-wa" aria-hidden>
                  <FaWhatsapp />
                </span>
                {templateImageUrl && (
                  <img
                    src={templateImageUrl}
                    alt=""
                    className="campaign-detail-template-img"
                  />
                )}
                {templatePreview.header && (
                  <p className="campaign-detail-preview-header">{templatePreview.header}</p>
                )}
                {templatePreview.body ? (
                  <p className="campaign-detail-preview-body">{templatePreview.body}</p>
                ) : (
                  !templateImageUrl && (
                    <p className="campaign-detail-preview-empty">
                      No template content is available for this campaign.
                    </p>
                  )
                )}
                {templatePreview.buttons.length > 0 && (
                  <div className="campaign-detail-preview-buttons">
                    {templatePreview.buttons.map((label) => (
                      <span key={label}>{label}</span>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignDetail;
